import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { useAtomValue } from "jotai";
import { templateKey } from "@ortex-charts/ui";
import { userDataState } from "@/lib/store";
import { getDefault, getPreference, loadLocalPreferences, loadPreferences, preferencesLoaded, savePreference, subscribePreferences, type PreferenceDoc } from "./preferences-store";

/**
 * Chart preferences: what a user chose for a *kind* of page.
 *
 * The key is the URL pattern with the instrument wildcarded, so
 * /s/NYSE/AMC/short-interest and /s/NASDAQ/TSLA/short-interest share one
 * document, plus a chart id for pages with several charts. The document is
 * whatever the chart wants to remember (lines, template, scale placement).
 * Storage, and when the platform is involved, live in ./preferences-store.
 */
export * from "./preferences-store";

/** Pages whose instrument segments are wildcarded; anything else keys on its own path. */
export const PAGE_PATTERNS: string[] = ["/s/*/*", "/s/*/*/**", "/id/*", "/id/*/**", "/trading-ideas/*", "/activist-filer/*", "/custom-views/**"];

/**
 * How long to wait for the account before asking the platform anyway. The app normally knows the
 * account well before a chart mounts; this only keeps a chart from waiting forever if the user
 * request fails (the platform then answers a visitor with 403, as it did before).
 */
const ACCOUNT_WAIT_MS = 8000;

/** `/s/*\/*\/short-interest#daily` for `/s/NYSE/AMC/short-interest` and chart id `daily`. */
export function preferenceKey(pathname: string, chartId?: string): string {
    const base = templateKey(pathname, PAGE_PATTERNS) || "/";
    return chartId ? `${base}#${chartId}` : base;
}

/**
 * The preference document for a chart on the current page, layered over the
 * staff default for the same key and then over `fallback`. Returns the
 * effective document, a setter that merges a patch, and a reset.
 */
export function useChartPreference<T extends PreferenceDoc>(chartId: string | undefined, fallback: T): [T, (patch: Partial<T>) => void, () => void, { key: string; loaded: boolean; isCustom: boolean }] {
    const router = useRouter();
    const key = useMemo(() => preferenceKey(router?.asPath ?? (typeof window !== "undefined" ? window.location.pathname : "/"), chartId), [router?.asPath, chartId]);
    const [, bump] = useState(0);
    // undefined until the account is fetched; a visitor's plan is "ORTEX Visitor".
    const user = useAtomValue(userDataState);
    const userKnown = user !== undefined;
    const signedIn = Boolean(user) && user.subscription_plan !== "ORTEX Visitor";
    useEffect(() => {
        const unsubscribe = subscribePreferences(() => bump((n) => n + 1));
        // localStorage straight away; the platform waits for the account below.
        loadLocalPreferences();
        return unsubscribe;
    }, []);
    useEffect(() => {
        if (userKnown) {
            void loadPreferences(signedIn);
            return;
        }
        const timer = setTimeout(() => void loadPreferences(true), ACCOUNT_WAIT_MS);
        return () => clearTimeout(timer);
    }, [userKnown, signedIn]);
    const own = getPreference(key);
    const def = getDefault(key);
    const doc = useMemo(
        () =>
            ({
                ...fallback,
                ...(def ?? {}),
                ...(own ?? {}),
            }) as T,
        [fallback, def, own]
    );
    const set = useCallback(
        (patch: Partial<T>) => {
            const current = { ...fallback, ...(getDefault(key) ?? {}), ...(getPreference(key) ?? {}) } as T;
            savePreference(key, { ...current, ...patch });
        },
        [key, fallback]
    );
    const reset = useCallback(() => savePreference(key, null), [key]);
    return [doc, set, reset, { key, loaded: preferencesLoaded(), isCustom: Boolean(own) }];
}
